/*
 * [NWF] Nähratgeber — runtime behaviour for the guide layout.
 *
 * Five jobs, all of them consequences of one architectural decision: every
 * Abschnitt is its own Shopify section (docs/naehratgeber-migration-plan.md §3.1).
 *
 *   1. Wrap all guide body sections into a two-column layout, so the contents
 *      column can be `position: sticky` across the WHOLE article. A sticky
 *      element is clipped by its containing block; if the list lived inside the
 *      first Abschnitt it would stop sticking when that Abschnitt scrolled past.
 *   2. Build the contents list and number the Abschnitte. Neither a CSS counter
 *      nor a Liquid loop can see across sibling sections.
 *   3. Scroll-spy the active entry.
 *   4. Drive the Bildstrecke slider (module 09).
 *   5. Fall back on the YouTube thumbnail when maxresdefault.jpg is missing.
 *
 * Deliberately NOT here: playing the video (that is the theme's <video-element>)
 * and any kind of image zoom — guide pictures are not enlargeable at all, see the
 * rationale in snippets/nwf-guide-image.liquid.
 *
 * Everything degrades: without this file the sections render in order, single
 * column, unnumbered and without a contents list. The text is all there.
 */

(() => {
    'use strict';

    const BODY = '[data-nwf-guide-body]';
    const LAYOUT_CLASS = 'nwf-guide__layout';

    /* ------------------------------------------------------------------ *
     * Layout
     * ------------------------------------------------------------------ */

    /* Shopify wraps every section in its own #shopify-section-* div and, on
     * `shopify:section:load`, re-renders the INSIDE of that div. Moving the
     * wrapper (not the inner markup) therefore survives an editor reload. */
    const shell = (el) => el.closest('.shopify-section') || el;

    const bodies = () => Array.from(document.querySelectorAll(BODY), shell);

    const unwrap = () => {
        document.querySelectorAll('.' + LAYOUT_CLASS).forEach((layout) => {
            const main = layout.querySelector('.nwf-guide__main');
            if (main) {
                while (main.firstChild) {
                    layout.parentNode.insertBefore(main.firstChild, layout);
                }
            }
            layout.remove();
        });
    };

    const wrap = () => {
        const sections = bodies();
        if (!sections.length) return null;

        const first = sections[0];
        const layout = document.createElement('div');
        layout.className = LAYOUT_CLASS;

        const aside = document.createElement('aside');
        /* sticky-element is the theme's own class. It resolves where a sticky box
         * belongs under this shop's header in all four states the header can be
         * in — pinned, unpinned, navigation collapsed, sticky-on-scroll-up — and
         * header.js keeps the variables behind it current. nwf-guide.css only
         * supplies --sticky-offset, the gap below the header. */
        aside.className = 'nwf-guide__toc sticky-element';

        const main = document.createElement('div');
        main.className = 'nwf-guide__main';

        layout.append(aside, main);
        first.parentNode.insertBefore(layout, first);
        sections.forEach((s) => main.appendChild(s));

        return { layout, aside, main };
    };

    /* ------------------------------------------------------------------ *
     * Contents list + numbering
     * ------------------------------------------------------------------ */

    const buildToc = (aside, main) => {
        const headings = Array.from(main.querySelectorAll('[data-nwf-guide-heading]'));
        /* One Abschnitt still gets a contents list. The earlier `< 2` guard dropped the
         * whole aside, and because the numbering below runs on the same list it also left
         * the number badge empty — the guide then rendered with an empty accent box in
         * front of the heading and no navigation at all. abnaeher is that case: a single
         * Abschnitt with one carousel. A list with one entry is a small oddity; a broken
         * heading is not, and the entry still shows where the reader is. */
        if (!headings.length) {
            aside.remove();
            return [];
        }

        headings.forEach((h, i) => {
            if (!h.id) h.id = 'nwf-guide-section-' + (i + 1);

            /* Bare, not zero-padded. The design set the Abschnitt number as "01"
             * and the contents entry as "1", but once the number became a badge
             * of its own the two are read as a pair — you click 4 in the list and
             * expect to see 4 above the heading. A guide has eight or nine
             * Abschnitte, so "01" also promises a two-digit series that never
             * arrives. Even badge widths come from min-width in the CSS, not from
             * padding the string. */
            const slot = shell(h).querySelector('[data-nwf-guide-number]');
            if (slot) slot.textContent = String(i + 1);
        });

        /* Labels come from the header section's data attributes, which Liquid
         * fills from the nwf.guide.* locale keys — this file must not carry
         * German strings of its own. */
        const strings = document.querySelector('[data-nwf-guide-labels]')?.dataset || {};

        const label = document.createElement('div');
        label.className = 'nwf-guide-overline nwf-guide__toc-label';
        label.textContent = strings.tocLabel || 'Inhalt';

        const list = document.createElement('ol');
        list.className = 'nwf-guide__toc-list';

        const links = headings.map((h, i) => {
            const li = document.createElement('li');
            const a = document.createElement('a');
            a.className = 'nwf-guide__toc-link';
            a.href = '#' + h.id;

            const num = document.createElement('span');
            num.className = 'nwf-guide__toc-number';
            num.textContent = String(i + 1);

            /* [data-nwf-guide-title], not the h2 itself: the number badge lives
             * inside the heading (see nwf-guide-section.liquid), so textContent
             * would start with the digit and every entry would read "2Bündchen
             * annähen". The fallback covers a hand-written heading without the
             * wrapper span. */
            const text = (h.querySelector('[data-nwf-guide-title]') || h).textContent.trim();
            a.append(num, document.createTextNode(text));
            li.appendChild(a);
            list.appendChild(li);
            return a;
        });

        /* Mobile gets a disclosure, desktop the plain list. One markup, the
         * summary is hidden by CSS above 1100px and <details> stays open. */
        const details = document.createElement('details');
        details.className = 'nwf-guide__toc-details';
        details.open = window.matchMedia('(min-width: 1100px)').matches;

        const summary = document.createElement('summary');
        summary.textContent = strings.tocLabelMobile || 'Inhalt dieses Ratgebers';

        details.append(summary, list);
        aside.append(label, details);

        return links;
    };

    /* ------------------------------------------------------------------ *
     * Scroll-spy
     * ------------------------------------------------------------------ */

    /* One reading line, and the active Abschnitt is the last one whose heading has
     * passed it. Deliberately NOT an IntersectionObserver over the section boxes.
     *
     * That was the first implementation and it was off by one on every click. It
     * observed the whole section shells through a band (rootMargin
     * `-96px 0px -60% 0px`) and marked the FIRST intersecting one active — so a
     * section needed only its last pixel inside the band to outrank the section
     * filling the rest of it. The Abschnitte are contiguous (padding_top 0), and
     * an anchor jump parks the heading at scroll-margin-top: 100px, four pixels
     * BELOW the band's top edge at 96px. The previous section therefore still had
     * those four pixels in the band and won. Measured: click 3 → 2 highlighted,
     * click 4 → 3, every time.
     *
     * The line is read back from the heading's own computed scroll-margin-top, not
     * from a second constant. An anchor jump puts the heading exactly at its
     * scroll-margin-top, so line and jump target are the same number by
     * construction and cannot drift apart again — including when the value grows
     * with the theme's --header-height. */
    const LINE_TOLERANCE = 2;

    let detachSpy = null;

    const spy = (links, main) => {
        /* build() runs again on every theme-editor section event, so the previous
         * listeners have to go — otherwise they pile up and keep painting into
         * links that are no longer in the document. */
        detachSpy?.();
        detachSpy = null;

        const heads = Array.from(main.querySelectorAll('[data-nwf-guide-heading]'));
        if (!links.length || heads.length !== links.length) return;

        const line = () => (parseFloat(getComputedStyle(heads[0]).scrollMarginTop) || 0) + LINE_TOLERANCE;

        let current = -1;

        /* The last Abschnitte are often short — "Fertig", a closing tip — and the
         * page simply runs out of scroll before their heading reaches the line, so
         * clicking the last contents entry would leave the one above it marked.
         * Once the page cannot scroll further, the deepest Abschnitt still on
         * screen is where the reader is. */
        const atBottom = () => window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

        const paint = () => {
            const y = line();

            /* Before the first heading reaches the line the reader is still in the
             * intro, and entry 1 stays marked — the list is never blank. */
            let active = 0;
            heads.forEach((h, i) => {
                if (h.getBoundingClientRect().top <= y) active = i;
            });

            if (atBottom()) {
                heads.forEach((h, i) => {
                    if (i > active && h.getBoundingClientRect().top < window.innerHeight) active = i;
                });
            }

            if (active === current) return;
            current = active;

            links.forEach((a, i) => {
                if (i === active) {
                    a.setAttribute('aria-current', 'true');
                } else {
                    a.removeAttribute('aria-current');
                }
            });
        };

        let queued = false;
        const onScroll = () => {
            if (queued) return;
            queued = true;
            requestAnimationFrame(() => {
                queued = false;
                paint();
            });
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        detachSpy = () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };

        paint();
    };

    /* ------------------------------------------------------------------ *
     * Module 09 — Bildstrecke
     * ------------------------------------------------------------------ */

    /* Two modes, split at the same breakpoint the theme splits at.
     *
     * Desktop is a transform track with arrows: a mouse has no gesture, so the
     * step has to be a control.
     *
     * Touch is the theme's .swipe-mobile pattern — the browser scrolls and snaps
     * (see nwf-guide.css, module 09), and this file only reads the position back
     * out to keep the counter, the dots and the Bildtext in step. That mirrors
     * what the theme's own sliders do below 768px: `if (FoxTheme.config.mqlMobile)
     * this.destroySlider()` in featured-collection.js, then CSS takes over.
     *
     * The query comes from FoxTheme.config so the two never diverge; the literal
     * is only for the moment before theme.js has run. */
    const mobileQuery = () => window.FoxTheme?.config?.mediaQueryMobile || 'screen and (max-width: 767px)';

    const initSlider = (root) => {
        if (root.dataset.nwfSliderReady) return;
        root.dataset.nwfSliderReady = '1';

        const track = root.querySelector('[data-nwf-slider-track]');
        const slides = Array.from(root.querySelectorAll('[data-nwf-slider-slide]'));
        if (!track || slides.length < 2) {
            /* A single slide needs none of the machinery below, but it does need its
             * Bildtext: the legend is painted from data-caption, so bailing out early
             * would drop the text of a one-picture Bildstrecke (kochschuerze ends with
             * one). The paging controls are hidden by .nwf-guide-slider--single. */
            const only = slides[0];
            const legend = root.querySelector('[data-nwf-slider-caption]');
            if (only && legend) legend.textContent = only.dataset.caption || '';
            return;
        }

        const viewport = root.querySelector('[data-nwf-slider-viewport]') || track.parentElement;
        const counter = root.querySelector('[data-nwf-slider-counter]');
        const caption = root.querySelector('[data-nwf-slider-caption]');
        const dotsWrap = root.querySelector('[data-nwf-slider-dots]');
        const prev = root.querySelector('[data-nwf-slider-prev]');
        const next = root.querySelector('[data-nwf-slider-next]');

        const mql = window.matchMedia(mobileQuery());
        let touch = mql.matches;
        let index = 0;

        const dots = slides.map((_, i) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'nwf-guide-slider__dot';
            b.setAttribute('aria-label', (dotsWrap?.dataset.dotLabel || 'Bild') + ' ' + (i + 1));
            b.addEventListener('click', () => go(i));
            dotsWrap?.appendChild(b);
            return b;
        });

        /* Everything that depends on which picture is showing, and nothing that
         * moves it — so it is safe to call from a scroll handler. */
        const paint = () => {
            if (counter) counter.textContent = index + 1 + ' / ' + slides.length;
            if (caption) caption.textContent = slides[index].dataset.caption || '';

            /* Dimmed at the ends, not removed. That is what the theme's own
             * sliders do: Swiper marks the dead button `swiper-button-disabled`
             * and vendor.css takes it to opacity .35 with pointer-events: none.
             * The native `disabled` attribute gets the same result plus the parts
             * a class cannot give — it is announced, and it drops out of the tab
             * order. Hiding the button instead would move the remaining one and
             * leave the reader wondering where it went. */
            if (prev) prev.disabled = index === 0;
            if (next) next.disabled = index === slides.length - 1;

            dots.forEach((d, i) => {
                if (i === index) {
                    d.setAttribute('aria-current', 'true');
                } else {
                    d.removeAttribute('aria-current');
                }
            });

            /* Only the transform track hides its neighbours. In the scroll mode
             * every slide is genuinely on the page and reachable by swiping, and
             * marking them inert would take them away from the reader who gets
             * there. */
            slides.forEach((s, i) => {
                const hidden = !touch && i !== index;
                s.toggleAttribute('inert', hidden);
                s.setAttribute('aria-hidden', String(hidden));
            });
        };

        /* Clamped, never wrapped — in BOTH modes.
         *
         * The transform mode used to wrap with a modulo, so "next" on the last
         * picture jumped back to the first and "previous" on the first jumped to
         * the last. Two things were wrong with that. It is not what this theme
         * does: every one of its sliders is built with `loop: false` (product
         * gallery, featured collection, collection list, image cards, lookbook) —
         * only press.js, a testimonial ticker, loops. And it did not even match
         * the guide's own touch mode, where a native scroll container simply has
         * ends. One component behaved two ways depending on the viewport.
         *
         * A Bildstrecke is a sequence of work steps. Step 8 is not followed by
         * step 1. */
        const go = (i) => {
            index = Math.min(Math.max(i, 0), slides.length - 1);
            if (touch) {
                viewport.scrollTo({ left: slides[index].offsetLeft - slides[0].offsetLeft });
            } else {
                track.style.transform = 'translateX(' + -index * 100 + '%)';
            }
            paint();
        };

        /* Snap points make this exact rather than a guess: the nearest slide start
         * IS where the scroll came to rest. */
        const readScroll = () => {
            const left = viewport.scrollLeft + slides[0].offsetLeft;
            let nearest = 0;
            let best = Infinity;
            slides.forEach((s, i) => {
                const d = Math.abs(s.offsetLeft - left);
                if (d < best) {
                    best = d;
                    nearest = i;
                }
            });
            if (nearest === index) return;
            index = nearest;
            paint();
        };

        let queued = false;
        viewport.addEventListener(
            'scroll',
            () => {
                if (!touch || queued) return;
                queued = true;
                requestAnimationFrame(() => {
                    queued = false;
                    readScroll();
                });
            },
            { passive: true },
        );

        prev?.addEventListener('click', () => go(index - 1));
        next?.addEventListener('click', () => go(index + 1));

        root.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') go(index - 1);
            if (e.key === 'ArrowRight') go(index + 1);
        });

        /* Rotating a phone, or dragging the theme-editor preview across the
         * breakpoint, swaps the mode. The leftovers of the other one have to go:
         * a stale transform would offset the scroll container, a stale scrollLeft
         * would offset the transform track. */
        mql.addEventListener('change', (e) => {
            touch = e.matches;
            track.style.transform = '';
            viewport.scrollLeft = 0;
            index = 0;
            go(0);
        });

        go(0);
    };

    /* ------------------------------------------------------------------ *
     * Module 13 — YouTube thumbnail fallback
     * ------------------------------------------------------------------ */

    /* maxresdefault.jpg only exists for videos uploaded in HD. The error event
     * does not bubble, so this listens in the capture phase, and the swap runs
     * once — otherwise a failing fallback would loop. */
    document.addEventListener(
        'error',
        (e) => {
            const img = e.target;
            if (!img?.matches?.('[data-nwf-video-thumb]')) return;
            const fallback = img.dataset.fallback;
            if (!fallback || img.dataset.fallbackUsed) return;
            img.dataset.fallbackUsed = '1';
            img.src = fallback;
        },
        true,
    );

    /* ------------------------------------------------------------------ *
     * Boot
     * ------------------------------------------------------------------ */

    const build = () => {
        unwrap();
        const parts = wrap();
        if (!parts) return;

        const links = buildToc(parts.aside, parts.main);
        spy(links, parts.main);
        parts.main.querySelectorAll('[data-nwf-slider]').forEach(initSlider);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', build, { once: true });
    } else {
        build();
    }

    /* Theme editor: a section that is added, removed or reordered lands outside
     * the wrapper, so the layout is rebuilt from scratch rather than patched. */
    document.addEventListener('shopify:section:load', build);
    document.addEventListener('shopify:section:unload', build);
    document.addEventListener('shopify:section:reorder', build);
})();
